/* eslint-disable max-lines */

import { ROLE } from "~/assets/js/constants";
import { isAllowedTo } from "~/assets/js/utils";
import { ALERT_STATUS } from "~/constants/alert";
import { EVOLUTION_RANGE } from "~/constants/dashboard";
import {
  toAgencyEvolution,
  toAgentsPerDayEvolution,
  toAlertEvolution,
  toAlertsPerDayEvolution,
  toAlertsPerVaccineEvolution,
  toCircle,
  toComputerEvolution,
  toDoughnutGroup,
  toPyramidGroup,
  toVaccineEvolution,
} from "~/transformers/dashboardTransformer";

function getBaseUrl(rootGetters) {
  const { agencyId } = rootGetters["application/getSelectedAgency"];
  const selectedInstance = rootGetters["application/getSelectedInstance"];

  if (selectedInstance?.instanceId) {
    return `instances/${selectedInstance.instanceId}/agencies/${selectedInstance.agencyId}/`;
  } else if (agencyId) {
    return `agencies/${agencyId}/`;
  }
  return "";
}

export default {
  async fetch({ dispatch, commit, rootGetters }) {
    dispatch("setLoading", true);
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const selectedInstance = rootGetters["application/getSelectedInstance"];
    const instanceId = selectedInstance?.instanceId;

    const isUser = isAllowedTo([ROLE.USER, ROLE.READONLY], rootGetters["application/getLoggedRole"]);

    await Promise.allSettled([
      dispatch("updateDoughnuts"),
      dispatch("updateCircles"),
      dispatch("updateLastAlerts"),
      ...(isUser ? [dispatch("updateLatestNews")] : []),
      dispatch("updatePyramids"),
      dispatch("instances/setInstances", {}, { root: true }),
    ]);

    try {
      if (!agencyId && !instanceId) {
        await dispatch("updateEvolutionStats", EVOLUTION_RANGE.LAST_7);
      }
    } catch (e) {
      console.error(e);
      this.$feedback.error("generic.evolutionStatsDataError");
      commit("SET_IS_STATS_EVOLUTION_ERROR", true);
    }
    dispatch("setLoading", false);
  },

  setLoading({ commit }, loading) {
    commit("SET_LOADING", loading);
  },

  async updateCircles({ commit, rootGetters }) {
    const circles = [];

    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const baseUrl = getBaseUrl(rootGetters);
    let endpoint;
    if (baseUrl.startsWith("instances/")) {
      endpoint = `${baseUrl}stats/totals`;
    } else {
      endpoint = agencyId ? `stats/${agencyId}/totals` : "stats/totals";
    }

    try {
      const totals = await this.$services.dashboard.totals(endpoint);

      circles.push(
        toCircle({
          index: 0,
          title: "dashboard.charts.totalAlerts.subtitle",
          subtitle: "dashboard.charts.totalAlerts.title",
          value: totals.alerts,
        }),
      );
      if (rootGetters["application/isAdmin"] && !agencyId && !baseUrl) {
        circles.push(
          toCircle({
            index: 3,
            title: "dashboard.charts.totalAgencies.subtitle",
            subtitle: "dashboard.charts.totalAgencies.title",
            value: totals.agencies,
          }),
        );
      }
      circles.push(
        toCircle({
          index: 1,
          title: "dashboard.charts.totalComputers.subtitle",
          subtitle: "dashboard.charts.totalComputers.title",
          value: totals.agents,
        }),
      );
      circles.push(
        toCircle({
          index: 2,
          title: "dashboard.charts.totalVaccines.subtitle",
          subtitle: "dashboard.charts.totalVaccines.title",
          value: totals.vaccines,
        }),
      );
      commit("SET_CIRCLES", circles);

      if (totals.lastUpdated !== undefined) {
        commit("application/UPDATE_SELECTED_INSTANCE_UPDATED_ON", totals.lastUpdated, {
          root: true,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug(e);
    }
  },

  async updateEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_COMPUTER_EVOLUTION_STATS", toComputerEvolution(stats));
    commit("SET_VACCINE_EVOLUTION_STATS", toVaccineEvolution(stats));
    commit("SET_ALERT_EVOLUTION_STATS", toAlertEvolution(stats));
    commit("SET_AGENCY_EVOLUTION_STATS", toAgencyEvolution(stats));
    commit("SET_ALERTS_PER_DAY_EVOLUTION_STATS", toAlertsPerDayEvolution(stats));
    commit("SET_AGENTS_PER_DAY_EVOLUTION_STATS", toAgentsPerDayEvolution(stats));
    commit("SET_ALERTS_PER_VACCINE_EVOLUTION_STATS", toAlertsPerVaccineEvolution(stats));
  },

  async updateComputerEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_COMPUTER_EVOLUTION_STATS", toComputerEvolution(stats));
  },

  async updateVaccineEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_VACCINE_EVOLUTION_STATS", toVaccineEvolution(stats));
  },

  async updateAlertEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_ALERT_EVOLUTION_STATS", toAlertEvolution(stats));
  },

  async updateAlertsPerDayEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_ALERTS_PER_DAY_EVOLUTION_STATS", toAlertsPerDayEvolution(stats));
  },

  async updateAgencyEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_AGENCY_EVOLUTION_STATS", toAgencyEvolution(stats));
  },

  async updateAgentsPerDayEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_AGENTS_PER_DAY_EVOLUTION_STATS", toAgentsPerDayEvolution(stats));
  },

  async updateAlertsPerVaccineEvolutionStats({ commit, rootGetters }, range) {
    const url = getBaseUrl(rootGetters);

    const stats = await this.$services.dashboard.evolution(url, range);
    commit("SET_ALERTS_PER_VACCINE_EVOLUTION_STATS", toAlertsPerVaccineEvolution(stats));
  },

  async updateDoughnuts({ commit, rootGetters }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const baseUrl = getBaseUrl(rootGetters);
    const url = agencyId ? `agencies/${agencyId}/` : baseUrl;

    const doughnuts = [];
    if (rootGetters["application/isAdmin"] && !agencyId && !baseUrl) {
      try {
        const items = await this.$services.dashboard.deploymentsCount({
          groupBy: "sector",
          size: 5,
        });
        doughnuts.push(
          toDoughnutGroup(items, {
            title: "dashboard.charts.bysector.title",
            measure: this.$i18n.t("computers.label"),
            emptyMessage: "dashboard.charts.bysector.error",
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
      try {
        const items = await this.$services.dashboard.deploymentsCount({
          groupBy: "agency",
          size: 5,
        });
        doughnuts.push(
          toDoughnutGroup(items, {
            title: "dashboard.charts.byagency.title",
            measure: this.$i18n.t("computers.label"),
            emptyMessage: "dashboard.charts.byagency.error",
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
      const items = await this.$services.dashboard.agentsCount("", {
        groupBy: "offline",
        size: 5,
      });
      doughnuts.push(
        toDoughnutGroup(items, {
          title: "dashboard.charts.offline.title",
          measure: this.$i18n.t("computers.label"),
          emptyMessage: "dashboard.charts.offline.error",
          alert: false,
        }),
      );
      try {
        const versionItems = await this.$services.dashboard.agentsCount("", {
          groupBy: "version",
          size: 5,
        });
        doughnuts.push(
          toDoughnutGroup(versionItems, {
            title: "dashboard.charts.versionall.title",
            measure: this.$i18n.t("computers.label"),
            emptyMessage: "dashboard.charts.versionall.error",
            alert: false,
            wilcardKey: "others",
            wilcardKeyTranslation: this.$i18n.t("generic.others"),
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
    } else {
      try {
        const items = await this.$services.dashboard.agentsCount(url, {
          groupBy: "status",
          size: 5,
        });
        const translatedItems = items.map((item) => ({
          ...item,
          name: this.$i18n.t(item.name),
        }));
        doughnuts.push(
          toDoughnutGroup(translatedItems, {
            title: "dashboard.charts.status.title",
            emptyMessage: "dashboard.charts.status.error",
            measure: this.$i18n.t("computers.label"),
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
      try {
        const items = await this.$services.dashboard.agentsCount(url, {
          groupBy: "version",
          size: 5,
        });
        doughnuts.push(
          toDoughnutGroup(items, {
            title: "dashboard.charts.version.title",
            emptyMessage: "dashboard.charts.version.error",
            measure: this.$i18n.t("computers.label"),
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
      try {
        const items = await this.$services.dashboard.agentsCount(url, {
          groupBy: "os",
          size: 5,
        });
        doughnuts.push(
          toDoughnutGroup(items, {
            title: "dashboard.charts.so.title",
            emptyMessage: "dashboard.charts.so.error",
            measure: this.$i18n.t("computers.label"),
            alert: false,
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug(e);
      }
    }
    commit("SET_DOUGHNUTS", doughnuts);
  },

  async updateLatestNews({ commit, rootGetters }) {
    try {
      const selectedInstance = rootGetters["application/getSelectedInstance"];
      const newsUrl = selectedInstance?.instanceId
        ? `instances/${selectedInstance.instanceId}/`
        : "";
      const newsItems = await this.$services.dashboard.news(newsUrl);
      commit("SET_NEWS", newsItems);
    } catch (error) {
      throw error;
    }
  },

  async updateLastAlerts({ state, commit, rootGetters }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const baseUrl = getBaseUrl(rootGetters);

    const url = agencyId ? `agencies/${agencyId}/` : baseUrl;

    try {
      const lastAlerts = await this.$services.dashboard.lastAlerts(url, {
        size: state.lastAlertsLength,
        filter: `status:${ALERT_STATUS.ACTIVE}`,
      });
      commit("SET_ALERTS", lastAlerts);
    } catch (error) {
      throw error;
    }
  },

  async setAlert({ commit, dispatch }, { status, id }) {
    try {
      const updatedAlert = await this.$services.dashboard.patchAlert(id, status);

      commit("SET_ALERT", updatedAlert);
      this.$feedback.ok("feedback.update.alert.ok");
      dispatch("updateLastAlerts");
    } catch (error) {
      this.$feedback.error("feedback.update.alert.error");
    }
  },

  async updatePyramids({ commit, rootGetters }) {
    const { agencyId } = rootGetters["application/getSelectedAgency"];
    const baseUrl = getBaseUrl(rootGetters);
    const url = agencyId ? `agencies/${agencyId}/` : baseUrl;

    const pyramids = [];
    if (rootGetters["application/isAdmin"] && !agencyId && !baseUrl) {
      try {
        let res = await this.$services.dashboard.alertsCount("", { groupBy: "sector", size: 8 });
        pyramids.push(
          toPyramidGroup(
            res,
            "dashboard.charts.alertsBySector.title",
            "dashboard.charts.alertsBySector.error",
            false,
          ),
        );

        res = await this.$services.dashboard.alertsCount("", { groupBy: "agency", size: 8 });
        pyramids.push(
          toPyramidGroup(
            res,
            "dashboard.charts.alertsByAgency.title",
            "dashboard.charts.alertsByAgency.error",
            false,
          ),
        );
      } catch (error) {
        throw new Error(error);
      }
    } else {
      try {
        const res = await this.$services.dashboard.alertsCount(url, {
          groupBy: "computer",
          size: 8,
        });
        pyramids.push(
          toPyramidGroup(
            res,
            "dashboard.charts.alertsByComputer.title",
            "dashboard.charts.alertsByComputer.error",
            false,
          ),
        );
      } catch (error) {
        throw new Error(error);
      }
    }

    try {
      const res = await this.$services.dashboard.alertsCount(url, { groupBy: "vaccine", size: 8 });
      pyramids.push(
        toPyramidGroup(
          res,
          "dashboard.charts.alertsByVaccine.title",
          "dashboard.charts.alertsByVaccine.error",
          false,
        ),
      );
    } catch (error) {
      throw error;
    }

    commit("SET_PYRAMIDS", pyramids);
  },
};
